/* eslint-disable no-param-reassign */
import {
    Box as MUIBox,
    TextField,
    MenuItem,
    Button,
    CircularProgress,
    FormControl,
    FormLabel,
    makeStyles,
    styled,
} from '@material-ui/core';
import {
    compose,
    spacing,
    palette,
    flexbox,
    shadows,
    display,
    sizing,
    positions,
    border,
    borders,
    color,
    grid,
    styleFunctionSx,
} from '@material-ui/system';
import {DescriptionOutlined, PublishOutlined} from '@material-ui/icons';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {v4 as uuid} from 'uuid';
import {CarrierOptions, ProductTypeOptions} from './SelectOptions';
import {DecimalNumberFormatCustom, NumberFormatCustom} from '../../Core/UI';
import PolicyFileUpload from './PolicyFileUpload';
import {Carrier, Policy, PolicyStatus, PolicyType, Document} from '../../APIs/CustomerPortalAPI';

const styleFunction = styleFunctionSx(
    compose(display, sizing, spacing, flexbox, shadows, palette, positions, color, border, borders, grid),
);

const Box = styled(MUIBox)(styleFunction);
const Flex = styled(Box)({display: 'flex'});

function nonNullable<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
}

const useStyles = makeStyles({
    wrapper: {},
    buttonTextSpacer: {
        marginLeft: '10px',
    },
    policyFileUpload: {
        fontSize: 12,
    },
});

const MONEY_REGEX = /^\d+(\.\d{1,2})?$/;

const PolicyDocument = {
    POLICY_INFORMATION: 'Policy Information',
    TERMS_AND_CONDITIONS: 'Terms & Conditions',
    ILLUSTRATION: 'Illustration',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
type PolicyDocument = (typeof PolicyDocument)[keyof typeof PolicyDocument];

const PolicySchema = z.object({
    type: z.nativeEnum(PolicyType),
    carrierName: z.nativeEnum(Carrier),
    number: z.string().min(1, 'Policy Number is required'),
    coverageAmount: z
        .string()
        .min(1, 'Coverage amount is required')
        .regex(MONEY_REGEX, 'Coverage amount must be a monetary value'),
    monthlyContribution: z
        .string()
        .min(1, 'Monthly contribution is required')
        .regex(MONEY_REGEX, 'Monthly contribution must be a monetary value'),
    policyInformationDocumentId: z.string().optional(),
    termsAndConditionsDocumentId: z.string().optional(),
    illustrationDocumentId: z.string().optional(),
});

type PolicyFormValues = z.infer<typeof PolicySchema>;

interface PolicyInformationFormProps {
    index: number;
    value: Policy;
    disabled?: boolean;
    onSubmit: (policy: Partial<Policy>) => Promise<void>;
    onCancel: (policy: Partial<Policy>) => void;
}

function PolicyInformationForm({value, index, disabled = false, onSubmit, onCancel}: PolicyInformationFormProps) {
    const classes = useStyles();

    const getDocumentByName = (name: PolicyDocument) => value.documents?.find((d) => d.name === name);

    const {register, handleSubmit, control, setValue, formState, reset} = useForm<PolicyFormValues>({
        resolver: zodResolver(PolicySchema),
        defaultValues: {
            ...value,
            policyInformationDocumentId: getDocumentByName(PolicyDocument.POLICY_INFORMATION)?.id,
            termsAndConditionsDocumentId: getDocumentByName(PolicyDocument.TERMS_AND_CONDITIONS)?.id,
            illustrationDocumentId: getDocumentByName(PolicyDocument.ILLUSTRATION)?.id,
        },
    });

    // eslint-disable-next-line arrow-body-style
    const s3keyGenerator = (file: File) => {
        return `policies/${value.id}/documents/${uuid()}/${file.name}`;
    };

    const onChangeDocument =
        (field: 'policyInformationDocumentId' | 'termsAndConditionsDocumentId' | 'illustrationDocumentId') =>
        (document: Document | null) => {
            setValue(field, document?.id, {shouldDirty: true});
        };

    return (
        <form
            onSubmit={handleSubmit(async (formValues) => {
                if (disabled) return;

                const {
                    policyInformationDocumentId,
                    termsAndConditionsDocumentId,
                    illustrationDocumentId,
                    ...restFormValues
                } = formValues;

                await onSubmit({
                    id: value.id,
                    customerId: value.customerId,
                    ...restFormValues,
                    /* prettier-ignore */
                    documentIds: [
                                policyInformationDocumentId,
                                termsAndConditionsDocumentId,
                                illustrationDocumentId
                            ].filter(nonNullable),
                });

                reset(formValues);
            })}
        >
            <Box>
                <Box>
                    <Flex sx={{alignItems: 'center'}}>
                        <DescriptionOutlined fontSize="small" />
                        <h4>Policy Information</h4>
                    </Flex>
                    <Flex sx={{gridGap: '24px'}}>
                        <Box sx={{flex: 1}}>
                            <Controller
                                control={control}
                                name="carrierName"
                                render={({field}) => (
                                    <TextField
                                        id="policy-carrier-name-input"
                                        {...field}
                                        select
                                        fullWidth
                                        label="Carrier Name"
                                        placeholder="--"
                                        variant="standard"
                                        error={!!formState.errors.carrierName}
                                        helperText={formState.errors.carrierName?.message}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    >
                                        {CarrierOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Controller
                                name="type"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        id="policy-type-input"
                                        {...field}
                                        select
                                        fullWidth
                                        label="Product Type"
                                        placeholder="--"
                                        variant="standard"
                                        error={!!formState.errors.type}
                                        helperText={formState.errors.type?.message}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    >
                                        {ProductTypeOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Box>
                        <Box sx={{flex: 0.75}}>
                            <TextField
                                id="policy-number-input"
                                {...register('number')}
                                label="Policy Number"
                                placeholder="--"
                                variant="standard"
                                error={!!formState.errors.number}
                                helperText={formState.errors.number?.message}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Controller
                                name="coverageAmount"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        id={`policy_coverage_amount_${index}`}
                                        {...field}
                                        label="Policy Coverage Amount"
                                        fullWidth
                                        placeholder="--"
                                        variant="standard"
                                        error={!!formState.errors.coverageAmount}
                                        helperText={formState.errors.coverageAmount?.message}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Controller
                                name="monthlyContribution"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        id={`monthly_contributions_${index}`}
                                        {...field}
                                        label="Monthly Contributions"
                                        fullWidth
                                        placeholder="--"
                                        variant="standard"
                                        error={!!formState.errors.monthlyContribution}
                                        helperText={formState.errors.monthlyContribution?.message}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            inputComponent: DecimalNumberFormatCustom,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Flex>
                </Box>
                <Box>
                    <Flex sx={{alignItems: 'center'}}>
                        <PublishOutlined fontSize="small" />
                        <h4>Document Upload</h4>
                    </Flex>
                    <Box display="flex" alignItems="center">
                        <Box sx={{width: '234px'}}>
                            <FormControl>
                                <FormLabel
                                    htmlFor="policy-terms-and-conditions-document"
                                    component="p"
                                    classes={{root: classes.policyFileUpload}}
                                >
                                    Terms & Conditions
                                </FormLabel>
                                <PolicyFileUpload
                                    id="policy-terms-and-conditions-document"
                                    name={PolicyDocument.TERMS_AND_CONDITIONS}
                                    value={getDocumentByName(PolicyDocument.TERMS_AND_CONDITIONS)}
                                    s3keyGenerator={s3keyGenerator}
                                    onChange={onChangeDocument('termsAndConditionsDocumentId')}
                                />
                            </FormControl>
                        </Box>
                        <Box sx={{width: '234px'}}>
                            <FormLabel
                                htmlFor="policy-information-document"
                                component="p"
                                classes={{root: classes.policyFileUpload}}
                            >
                                Policy Information
                            </FormLabel>
                            <PolicyFileUpload
                                id="policy-information-document"
                                name={PolicyDocument.POLICY_INFORMATION}
                                s3keyGenerator={s3keyGenerator}
                                value={getDocumentByName(PolicyDocument.POLICY_INFORMATION)}
                                onChange={onChangeDocument('policyInformationDocumentId')}
                            />
                        </Box>
                        <Box sx={{width: '234px'}}>
                            <FormLabel
                                htmlFor="policy-illustration-document"
                                component="p"
                                classes={{root: classes.policyFileUpload}}
                            >
                                Illustration
                            </FormLabel>
                            <PolicyFileUpload
                                id="policy-illustration-document"
                                name={PolicyDocument.ILLUSTRATION}
                                s3keyGenerator={s3keyGenerator}
                                value={getDocumentByName(PolicyDocument.ILLUSTRATION)}
                                onChange={onChangeDocument('illustrationDocumentId')}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                    {value.status === PolicyStatus.DRAFT && (
                        <Button id="cancel-add-policy-button" onClick={() => onCancel(value)}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        id="save-policy-button"
                        variant="outlined"
                        color="primary"
                        type="submit"
                        disabled={formState.isSubmitting || !formState.isDirty}
                    >
                        {!formState.isSubmitting ? (
                            <>Save Policy</>
                        ) : (
                            <>
                                <CircularProgress size={16} />
                                <Box component="span" className={classes.buttonTextSpacer}>
                                    Saving Policy...
                                </Box>
                            </>
                        )}
                    </Button>
                </Box>
            </Box>
        </form>
    );
}

export default PolicyInformationForm;
